import { Link } from "@inertiajs/react"

interface SubjectTypeOption {
  id: number
  name: string
}

interface SubjectFormData {
  subject_type_id: number | null
  type_description: string
  prefix: string
  first_name: string
  middle_name: string
  last_name: string
  suffix: string
  address: string
  address2: string
  subdivision: string
  address_dates: string
  city: string
  state: string
  zip: string
  home_phone: string
  cell_phone: string
  work_phone: string
  email: string
  drivers_license: string
  drivers_license_state: string
  drivers_license_expires: string | null
  drivers_license_name: string
  date_of_birth: string | null
  ssn: string | null
  sex: string
  race: string | null
  marital_status: string | null
  spouse_name: string
  divorce_date: string | null
  dependents: string
  law_firm: string
  attorney: string
  place_of_employment: string
  poe_address: string
  poe_city: string
  poe_state: string
  poe_zip: string
  work_schedule: string
  hobbies: string
  description: string
  internal_notes: string
  ins_date_of_loss: string
  ins_injury: string
  ins_limits: string
  ins_contact: string
  ins_contact_phone: string
  ins_rehab: string
  ins_may_we_contact: string
  ins_prev_surveillance: string
  ins_benefits: string
  ins_working: string
  ins_appts: string
  ins_accident: string
  ins_rehab_loc: string
  ins_appt_info: string
  ins_prev_surveillance_date: string
}

interface Props {
  caseId: number
  data: SubjectFormData
  setData: (key: keyof SubjectFormData, value: string | number | null) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  subject_types: SubjectTypeOption[]
}

const inputClass =
  "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
const labelClass = "block text-sm font-medium text-hpi-dark-gray"

function FieldError({
  errors,
  field,
}: {
  errors?: Record<string, string[]>
  field: string
}) {
  if (!errors?.[field]) return null
  return <p className="text-hpi-red mt-1 text-sm">{errors[field].join(", ")}</p>
}

export default function SubjectForm({
  caseId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  subject_types,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="max-w-5xl space-y-8">
      {/* Subject Type */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Subject Type</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Type</label>
            <select
              value={data.subject_type_id ?? ""}
              onChange={(e) =>
                setData(
                  "subject_type_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select type...</option>
              {subject_types.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name}
                </option>
              ))}
            </select>
            <FieldError errors={errors} field="subject_type_id" />
          </div>
          <div>
            <label className={labelClass}>Subtype</label>
            <input
              type="text"
              value={data.type_description}
              onChange={(e) => setData("type_description", e.target.value)}
              className={inputClass}
            />
            <FieldError errors={errors} field="type_description" />
          </div>
        </div>
      </fieldset>

      {/* Name */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Name</legend>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div>
            <label className={labelClass}>Prefix</label>
            <input
              type="text"
              value={data.prefix}
              onChange={(e) => setData("prefix", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              First Name <span className="text-hpi-red">*</span>
            </label>
            <input
              type="text"
              value={data.first_name}
              onChange={(e) => setData("first_name", e.target.value)}
              className={inputClass}
              required
            />
            <FieldError errors={errors} field="first_name" />
          </div>
          <div>
            <label className={labelClass}>Middle</label>
            <input
              type="text"
              value={data.middle_name}
              onChange={(e) => setData("middle_name", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              Last Name <span className="text-hpi-red">*</span>
            </label>
            <input
              type="text"
              value={data.last_name}
              onChange={(e) => setData("last_name", e.target.value)}
              className={inputClass}
              required
            />
            <FieldError errors={errors} field="last_name" />
          </div>
          <div>
            <label className={labelClass}>Suffix</label>
            <input
              type="text"
              value={data.suffix}
              onChange={(e) => setData("suffix", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* Address */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Address</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Address</label>
            <input
              type="text"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Address 2</label>
            <input
              type="text"
              value={data.address2}
              onChange={(e) => setData("address2", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Subdivision / Apartment</label>
            <input
              type="text"
              value={data.subdivision}
              onChange={(e) => setData("subdivision", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Dates at Address</label>
            <input
              type="text"
              value={data.address_dates}
              onChange={(e) => setData("address_dates", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>City</label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => setData("city", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <input
              type="text"
              value={data.state}
              onChange={(e) => setData("state", e.target.value)}
              className={inputClass}
              maxLength={2}
            />
          </div>
          <div>
            <label className={labelClass}>Zip</label>
            <input
              type="text"
              value={data.zip}
              onChange={(e) => setData("zip", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* Contact Information */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Contact Information</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Home Phone</label>
            <input
              type="tel"
              value={data.home_phone}
              onChange={(e) => setData("home_phone", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Cell Phone</label>
            <input
              type="tel"
              value={data.cell_phone}
              onChange={(e) => setData("cell_phone", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Work Phone</label>
            <input
              type="tel"
              value={data.work_phone}
              onChange={(e) => setData("work_phone", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              className={inputClass}
            />
            <FieldError errors={errors} field="email" />
          </div>
        </div>
      </fieldset>

      {/* Personal Details */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Personal Details</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>SSN</label>
            <input
              type="text"
              value={data.ssn ?? ""}
              onChange={(e) => setData("ssn", e.target.value || null)}
              className={inputClass}
              maxLength={11}
              placeholder="xxx-xx-xxxx"
            />
            <FieldError errors={errors} field="ssn" />
          </div>
          <div>
            <label className={labelClass}>Date of Birth</label>
            <input
              type="date"
              value={data.date_of_birth ?? ""}
              onChange={(e) => setData("date_of_birth", e.target.value || null)}
              className={inputClass}
            />
            <FieldError errors={errors} field="date_of_birth" />
          </div>
          <div>
            <label className={labelClass}>Sex</label>
            <select
              value={data.sex}
              onChange={(e) => setData("sex", e.target.value)}
              className={inputClass}
            >
              <option value="">Select...</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Ethnicity</label>
            <select
              value={data.race ?? ""}
              onChange={(e) => setData("race", e.target.value || null)}
              className={inputClass}
            >
              <option value="">Select...</option>
              <option value="Caucasian">Caucasian</option>
              <option value="African American">African American</option>
              <option value="Hispanic">Hispanic</option>
              <option value="Asian">Asian</option>
              <option value="Native American">Native American</option>
              <option value="Pacific Islander">Pacific Islander</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
            className={inputClass}
            rows={3}
          />
        </div>
        <div>
          <label className={labelClass}>Details / Hobbies</label>
          <textarea
            value={data.hobbies}
            onChange={(e) => setData("hobbies", e.target.value)}
            className={inputClass}
            rows={3}
          />
        </div>
      </fieldset>

      {/* Attorney */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Attorney</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>
              Law Firm{" "}
              {data.subject_type_id === 1 && (
                <span className="text-hpi-red">*</span>
              )}
            </label>
            <input
              type="text"
              value={data.law_firm}
              onChange={(e) => setData("law_firm", e.target.value)}
              className={inputClass}
              required={data.subject_type_id === 1}
              maxLength={50}
            />
            <FieldError errors={errors} field="law_firm" />
          </div>
          <div>
            <label className={labelClass}>
              Attorney Name{" "}
              {data.subject_type_id === 1 && (
                <span className="text-hpi-red">*</span>
              )}
            </label>
            <input
              type="text"
              value={data.attorney}
              onChange={(e) => setData("attorney", e.target.value)}
              className={inputClass}
              required={data.subject_type_id === 1}
              maxLength={50}
            />
            <FieldError errors={errors} field="attorney" />
          </div>
        </div>
      </fieldset>

      {/* Family */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Family</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className={labelClass}>Marital Status</label>
            <select
              value={data.marital_status ?? ""}
              onChange={(e) =>
                setData("marital_status", e.target.value || null)
              }
              className={inputClass}
            >
              <option value="">Select...</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Separated">Separated</option>
              <option value="Widowed">Widowed</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Spouse Name</label>
            <input
              type="text"
              value={data.spouse_name}
              onChange={(e) => setData("spouse_name", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Divorce Date</label>
            <input
              type="date"
              value={data.divorce_date ?? ""}
              onChange={(e) => setData("divorce_date", e.target.value || null)}
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Dependents</label>
          <textarea
            value={data.dependents}
            onChange={(e) => setData("dependents", e.target.value)}
            className={inputClass}
            rows={2}
          />
        </div>
      </fieldset>

      {/* Driver's License */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Driver&apos;s License</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>License Number</label>
            <input
              type="text"
              value={data.drivers_license}
              onChange={(e) => setData("drivers_license", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <input
              type="text"
              value={data.drivers_license_state}
              onChange={(e) => setData("drivers_license_state", e.target.value)}
              className={inputClass}
              maxLength={2}
            />
          </div>
          <div>
            <label className={labelClass}>Name on License</label>
            <input
              type="text"
              value={data.drivers_license_name}
              onChange={(e) => setData("drivers_license_name", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Expires</label>
            <input
              type="date"
              value={data.drivers_license_expires ?? ""}
              onChange={(e) =>
                setData("drivers_license_expires", e.target.value || null)
              }
              className={inputClass}
            />
          </div>
        </div>
      </fieldset>

      {/* Employment */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Employment</legend>
        <div>
          <label className={labelClass}>Place of Employment</label>
          <input
            type="text"
            value={data.place_of_employment}
            onChange={(e) => setData("place_of_employment", e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Address</label>
            <input
              type="text"
              value={data.poe_address}
              onChange={(e) => setData("poe_address", e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>City</label>
              <input
                type="text"
                value={data.poe_city}
                onChange={(e) => setData("poe_city", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>State</label>
              <input
                type="text"
                value={data.poe_state}
                onChange={(e) => setData("poe_state", e.target.value)}
                className={inputClass}
                maxLength={2}
              />
            </div>
            <div>
              <label className={labelClass}>Zip</label>
              <input
                type="text"
                value={data.poe_zip}
                onChange={(e) => setData("poe_zip", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </div>
        <div>
          <label className={labelClass}>Work Schedule</label>
          <input
            type="text"
            value={data.work_schedule}
            onChange={(e) => setData("work_schedule", e.target.value)}
            className={inputClass}
          />
        </div>
      </fieldset>

      {/* Insurance Claims */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Insurance Claims</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Date of Loss</label>
            <input
              type="text"
              value={data.ins_date_of_loss}
              onChange={(e) => setData("ins_date_of_loss", e.target.value)}
              className={inputClass}
              maxLength={12}
            />
          </div>
          <div>
            <label className={labelClass}>Injury</label>
            <input
              type="text"
              value={data.ins_injury}
              onChange={(e) => setData("ins_injury", e.target.value)}
              className={inputClass}
              maxLength={50}
            />
          </div>
          <div>
            <label className={labelClass}>Restrictions</label>
            <input
              type="text"
              value={data.ins_limits}
              onChange={(e) => setData("ins_limits", e.target.value)}
              className={inputClass}
              maxLength={50}
            />
          </div>
          <div>
            <label className={labelClass}>Contact Person</label>
            <input
              type="text"
              value={data.ins_contact}
              onChange={(e) => setData("ins_contact", e.target.value)}
              className={inputClass}
              maxLength={50}
            />
          </div>
          <div>
            <label className={labelClass}>Contact Phone</label>
            <input
              type="tel"
              value={data.ins_contact_phone}
              onChange={(e) => setData("ins_contact_phone", e.target.value)}
              className={inputClass}
              maxLength={50}
            />
          </div>
        </div>
        <div className="bg-hpi-pale-gray space-y-2 rounded p-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.ins_rehab === "1"}
              onChange={(e) =>
                setData("ins_rehab", e.target.checked ? "1" : "")
              }
            />
            Going to Rehab
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.ins_may_we_contact === "1"}
              onChange={(e) =>
                setData("ins_may_we_contact", e.target.checked ? "1" : "")
              }
            />
            HPI may contact employer
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.ins_prev_surveillance === "1"}
              onChange={(e) =>
                setData("ins_prev_surveillance", e.target.checked ? "1" : "")
              }
            />
            Previous Surveillance
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.ins_benefits === "1"}
              onChange={(e) =>
                setData("ins_benefits", e.target.checked ? "1" : "")
              }
            />
            Receiving Benefits
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.ins_working === "1"}
              onChange={(e) =>
                setData("ins_working", e.target.checked ? "1" : "")
              }
            />
            Working Light Duty
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.ins_appts === "1"}
              onChange={(e) =>
                setData("ins_appts", e.target.checked ? "1" : "")
              }
            />
            Scheduled Appointments
          </label>
        </div>
        <div>
          <label className={labelClass}>Accident</label>
          <textarea
            value={data.ins_accident}
            onChange={(e) => setData("ins_accident", e.target.value)}
            className={inputClass}
            rows={3}
          />
        </div>
        <div>
          <label className={labelClass}>Rehab Facility & Schedule</label>
          <textarea
            value={data.ins_rehab_loc}
            onChange={(e) => setData("ins_rehab_loc", e.target.value)}
            className={inputClass}
            rows={3}
          />
        </div>
        <div>
          <label className={labelClass}>Scheduled Appointments</label>
          <textarea
            value={data.ins_appt_info}
            onChange={(e) => setData("ins_appt_info", e.target.value)}
            className={inputClass}
            rows={3}
          />
        </div>
        <div>
          <label className={labelClass}>Dates of Previous Surveillance</label>
          <input
            type="text"
            value={data.ins_prev_surveillance_date}
            onChange={(e) =>
              setData("ins_prev_surveillance_date", e.target.value)
            }
            className={inputClass}
            maxLength={50}
          />
        </div>
      </fieldset>

      {/* Internal Notes */}
      <fieldset className="space-y-4">
        <legend className="text-lg font-medium">Internal Notes</legend>
        <div>
          <textarea
            value={data.internal_notes}
            onChange={(e) => setData("internal_notes", e.target.value)}
            className={inputClass}
            rows={4}
            placeholder="Staff-only notes..."
          />
        </div>
      </fieldset>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {processing ? "Saving..." : submitLabel}
        </button>
        <Link
          href={`/cases/${caseId}/subjects`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}

export type { SubjectFormData, SubjectTypeOption }
